import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { AddOnVisibilityOperationType, DialogType } from '../../common/enums';
import { fetchAddOnsVisibilities } from '../../common/fetch';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  additionalServicesMsg,
  editAddonsMsg,
  instructionsForAddonsMsg,
  instructionsMsg,
  learnMoreAboutServicesMsg,
  t,
} from '../../common/i18n';
import { isFeatureEnabled } from '../../common/utils/featureFlagUtils';
import {
  getAddonAssociationsAndDependencies,
  isChangeOfferAllowed,
  isExternalSubscription,
  isMobileIdAddOn,
  isReachabilityWithMobile,
  isSubscriptionInSaunalahti,
  shouldDisplayMobileReachabilityAddon,
} from '../../common/utils/subscriptionUtils';
import { Contract } from '../../generated/api/contract';
import { getCategoryBySubscriptionType } from '../../public/common/util/category';
import { useConfig } from '../../public/site/ConfigProvider.js';
import { openDialog } from '../../public/site/dialogStore.js';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { HeaderWithEditButton } from '../HeaderWithButton/HeaderWithButton';
import { Loading } from '../Loading';
import { AdditionalServicesContent } from './AdditionalServicesContent';
import { SaunalahtiCardContent } from './Components/CardContent';
import { MOBIILITURVA_ADDON, isAddonVisible, updateVisibilitiesIfRing } from './addOnDependencyUtils';
import { isLiikkuvaWifiSubscription } from './subscriptionDetailsUtils';
import type { BaseRules } from './addOnDependencyUtils';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { AddOnVisibility } from '../../generated/api/addOnVisibility';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Subscription } from '../../generated/api/subscription';

export interface MobileSubscriptionAddonsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
  companyInfo?: CompanyInfoResponse;
  addOnRules?: AddOnRulesResponse;
  addOnRulesMobilePbx?: AddOnRulesResponse;
  mobileIdContracts?: Contract[];
}

export const MobileSubscriptionAddons = ({
  subscription,
  hasPendingActions,
  companyInfo,
  addOnRules,
  addOnRulesMobilePbx,
  mobileIdContracts: mobileIdContractsActual,
}: MobileSubscriptionAddonsProps) => {
  const config = useConfig();
  const [isEditing, setEditing] = useState(false);
  const [addOnVisibilities, setAddOnVisibilities] = useState<AddOnVisibility[] | undefined>(undefined);
  const [loadingAddOnVisibility, setLoadingAddOnVisibility] = useState(true);
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const mobileIdContracts = isFeatureEnabled(config?.featureFlags?.mobileId) ? mobileIdContractsActual : undefined;
  const isAssetWithMobiiliturva = subscription.details?.asset?.hasMobiiliturva ?? false;
  const { associations, dependencies } = getAddonAssociationsAndDependencies(
    subscription,
    addOnRules,
    addOnRulesMobilePbx
  );
  const changeOfferAllowed = isChangeOfferAllowed(subscription, hasPendingActions);

  useEffect(() => {
    if (!addOnVisibilities) {
      // Assets (Tellus based subscriptions) may have Mobiiliturva addon but no commercial product, which could be used to fetch the addon data,
      // so put a hard coded version to visibility object list.
      if (isExternalSubscription(subscription)) {
        setAddOnVisibilities([MOBIILITURVA_ADDON]);
        setLoadingAddOnVisibility(false);
      } else if (isLiikkuvaWifiSubscription(subscription.subscriptionName)) {
        setAddOnVisibilities([]);
        setLoadingAddOnVisibility(false);
      } else {
        setLoadingAddOnVisibility(true);
        fetchAddOnsVisibilities(
          subscription.commercialProductCode || '',
          AddOnVisibilityOperationType.CHANGE,
          mdmId
        ).then(res => {
          setAddOnVisibilities(res.addOnVisibilities);
          setLoadingAddOnVisibility(false);
        });
      }
    }
  }, [
    mdmId,
    addOnVisibilities,
    isAssetWithMobiiliturva,
    subscription,
    subscription.commercialProductCode,
    subscription.details?.selectedAddOns,
    subscription.subscriptionName,
  ]);

  const baseRules: BaseRules = {
    commercialProductId: subscription.commercialProductId || '',
    commercialProductCode: subscription.commercialProductCode || '',
    addOnAssociations: associations ?? [],
    addOnDependencies: dependencies ?? [],
  };
  const updatedVisibilities =
    updateVisibilitiesIfRing(subscription, companyInfo?.pbxSolutions ?? [], addOnVisibilities) ?? [];
  const isMobileIdEnabledForCompany =
    mobileIdContracts?.some(contract => contract.contractStatus === Contract.ContractStatusEnum.ACTIVATED) ?? false;
  const showMobileReachabilityAddon = shouldDisplayMobileReachabilityAddon(
    config,
    subscription,
    companyInfo?.pbxSolutions ?? []
  );
  const subscriptionAddOnCodes = subscription.details?.selectedAddOns?.map(addon => addon.addOnCode) || [];
  const addonsToDisplay: AddOnVisibility[] = updatedVisibilities
    .filter(addOnVisibility => isAddonVisible(isAssetWithMobiiliturva, subscriptionAddOnCodes, addOnVisibility, false))
    .filter(addOnVisibility => !isMobileIdAddOn(addOnVisibility.addOn?.addOnGroup))
    .filter(
      addOnVisibility => showMobileReachabilityAddon || !isReachabilityWithMobile(addOnVisibility.addOn?.addOnCode)
    );
  const isAllDataLoaded = Boolean(!loadingAddOnVisibility && associations && dependencies);
  return (
    <DetailsWithCard
      cardContent={
        isSubscriptionInSaunalahti(subscription) ? (
          <SaunalahtiCardContent />
        ) : (
          <>
            <h3>{t.RD4R(instructionsMsg)}</h3>
            <p>
              {t.CPUS(instructionsForAddonsMsg)}
              <br />
              <Link to={paths.INSTRUCTIONS_SUBSCRIPTION_ADDONS} target="_blank" rel="noopener noreferrer">
                {t.NCME(learnMoreAboutServicesMsg)}
              </Link>
            </p>
          </>
        )
      }
    >
      <HeaderWithEditButton
        title={t.LXSR(additionalServicesMsg)}
        showButton={!isEditing && addonsToDisplay.length > 0}
        buttonText={t.BG4W(editAddonsMsg)}
        onClick={() => {
          if (changeOfferAllowed) {
            setEditing(!isEditing);
          } else {
            openDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
          }
        }}
      />
      {isAllDataLoaded ? (
        <AdditionalServicesContent
          category={getCategoryBySubscriptionType(subscription.subscriptionType)}
          changeOfferAllowed={changeOfferAllowed}
          subscription={subscription}
          isMobileIdEnabledForCompany={isMobileIdEnabledForCompany}
          isMobileIdEnabledForEoeUser={false}
          addOnVisibilities={updatedVisibilities}
          baseRules={baseRules}
          isEmployee={false}
          isAssetWithMobiiliturva={isAssetWithMobiiliturva}
          showMobileReachabilityAddon={shouldDisplayMobileReachabilityAddon(
            config,
            subscription,
            companyInfo?.pbxSolutions ?? []
          )}
          isEditing={isEditing}
          setEditing={setEditing}
          hideEditButton={true}
        />
      ) : (
        <Loading />
      )}
    </DetailsWithCard>
  );
};
